import { useState, useEffect } from "react";

export default function Orders({ supabase }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // M1c-1 refund tool state
  const [failedRefunds, setFailedRefunds] = useState([]);
  const [refundCtx, setRefundCtx] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundMsg, setRefundMsg] = useState(null);

  const [wdRows, setWdRows] = useState([]);
  const [inflightBalance, setInflightBalance] = useState(0);
  const [debts, setDebts] = useState([]);
  const [pendingRefunds, setPendingRefunds] = useState([]);
  const [sweepFails, setSweepFails] = useState([]);

  useEffect(() => { loadOrders(); loadFailedRefunds(); loadWithdrawalHealth(); }, []);

  // M3c: withdrawals needing attention. Failed = money promised that
  // didn't arrive; pending/submitted = money sitting in payout_inflight
  // (a stuck 'submitted' means a callback never came). The inflight
  // balance is the money-in-limbo figure and should trend to zero.
  async function loadWithdrawalHealth() {
    const { data: rows } = await supabase.from("withdrawals")
      .select("id, profile_id, amount_sle, phone, reason, status, created_at, profiles(full_name)")
      .in("status", ["failed", "pending", "submitted"])
      .order("created_at", { ascending: false });
    setWdRows(rows || []);
    // M4a-2: provider debts — negative balances are receivables created
    // by refund reversals after payout (ruling: visible, never hidden).
    const { data: accts } = await supabase.from("ledger_accounts")
      .select("id, profile_id, profiles(full_name)").eq("kind", "provider");
    if (accts?.length) {
      const { data: allEnts } = await supabase.from("ledger_entries")
        .select("account_id, amount").in("account_id", accts.map(a => a.id));
      const sums = {};
      for (const e of allEnts || []) sums[e.account_id] = (sums[e.account_id] || 0) + Number(e.amount);
      setDebts(accts
        .map(a => ({ ...a, balance: sums[a.id] || 0 }))
        .filter(a => a.balance < 0));
    } else setDebts([]);
    const { data: acct } = await supabase.from("ledger_accounts")
      .select("id").eq("kind", "payout_inflight").maybeSingle();
    if (acct) {
      const { data: ents } = await supabase.from("ledger_entries")
        .select("amount").eq("account_id", acct.id);
      setInflightBalance((ents || []).reduce((s, e) => s + Number(e.amount), 0));
    }
  }

  async function retryWithdrawal(withdrawalId) {
    const { data, error } = await supabase.functions.invoke("pawapay-withdraw", { body: { withdrawalId } });
    if (error) window.alert(`Retry failed: ${await fnErrorText(error)}`);
    else window.alert(`Submitted (${data?.status || "ACCEPTED"}); completion arrives via callback.`);
    loadWithdrawalHealth();
  }

  // Refund context loads per opened order. refund_context is the single
  // source for verified-paid figures (M1c-0): its remainder uses the same
  // arithmetic as admin_refund's guard, so clamping the input to it means
  // the RPC can never reject on amount.
  useEffect(() => {
    setRefundCtx(null); setRefundAmt(""); setRefundReason(""); setRefundMsg(null);
    if (selected?.id) loadContext(selected.id);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadContext(orderId) {
    const { data, error } = await supabase.rpc("refund_context", { p_order_id: orderId });
    if (error) { setRefundCtx({ error: error.message }); return; }
    const row = Array.isArray(data) ? data[0] : data;
    setRefundCtx(row || { error: "No context returned" });
  }

  async function loadFailedRefunds() {
    const { data } = await supabase.from("refunds")
      .select("id, order_id, amount_sle, reason, created_at")
      .eq("status", "failed")
      .order("created_at", { ascending: false });
    setFailedRefunds(data || []);
    // M4b-2: pawapay refunds minted but never submitted (e.g. by a 2am
    // sweep auto-cancel -- cron holds no keys, so nothing HTTP-submits).
    const { data: pend } = await supabase.from("refunds")
      .select("id, order_id, amount_sle, created_at")
      .eq("status", "pending").eq("method", "pawapay")
      .lt("created_at", new Date(Date.now() - 15 * 60000).toISOString())
      .order("created_at", { ascending: true });
    setPendingRefunds(pend || []);
    // M4b-2: sweep failures (7d) -- per-item isolation means the cron
    // job always exits clean, so sweep_log is the only failure record.
    const { data: sf } = await supabase.from("sweep_log")
      .select("sweep, item_id, error, created_at")
      .eq("ok", false)
      .gte("created_at", new Date(Date.now() - 7 * 86400000).toISOString())
      .order("created_at", { ascending: false })
      .limit(10);
    setSweepFails(sf || []);
  }

  // supabase-js buries the edge function's JSON error body; dig it out so
  // failures show PawaPay's actual complaint, not "non-2xx status code".
  async function fnErrorText(error) {
    try {
      const body = await error.context.json();
      return body.error || JSON.stringify(body);
    } catch { return error.message; }
  }

  async function submitPendingRefund(orderId) {
    setRefundBusy(true); setRefundMsg(null);
    const { data, error } = await supabase.functions.invoke("pawapay-refund", { body: { orderId } });
    if (error) setRefundMsg({ ok: false, text: `PawaPay submission failed: ${await fnErrorText(error)}` });
    else setRefundMsg({ ok: true, text: `Submitted to PawaPay (${data?.status || "ACCEPTED"}); completion arrives via callback.` });
    setRefundBusy(false);
    loadContext(orderId); loadFailedRefunds();
  }

  async function doRefund() {
    const amt = Number(refundAmt);
    if (!selected || !refundCtx || !(amt > 0)) return;
    const name = selected.profiles?.full_name || "the customer";
    const via = refundCtx.o_method === "wallet"
      ? `Le ${amt} returns to ${name}'s wallet instantly.`
      : `Le ${amt} goes back to the phone that paid, via PawaPay.`;
    if (!window.confirm(`Refund exactly Le ${amt} for order #${selected.id.slice(0, 8).toUpperCase()}?\n\n${via}`)) return;
    setRefundBusy(true); setRefundMsg(null);
    const { error } = await supabase.rpc("admin_refund", {
      p_order_id: selected.id, p_amount: amt, p_reason: refundReason || null,
    });
    if (error) { setRefundMsg({ ok: false, text: error.message }); setRefundBusy(false); return; }
    let text = `Refund of Le ${amt} recorded.`;
    if (refundCtx.o_method === "pawapay") {
      const { data: fnData, error: fnError } = await supabase.functions.invoke("pawapay-refund", { body: { orderId: selected.id } });
      if (fnError) text += ` PawaPay submission FAILED: ${await fnErrorText(fnError)} — the refund stays pending; use Submit below to retry.`;
      else text += ` Submitted to PawaPay (${fnData?.status || "ACCEPTED"}); completion arrives via callback.`;
    } else {
      text += " Wallet credited.";
    }
    setRefundMsg({ ok: true, text });
    setRefundAmt(""); setRefundReason(""); setRefundBusy(false);
    loadOrders(); loadFailedRefunds(); loadContext(selected.id);
  }

  async function loadOrders() {
    // orders has three FKs into profiles (customer/driver/assigned driver),
    // so the embeds must name their FK or PostgREST rejects the query.
    const { data, error } = await supabase
      .from("orders")
      .select("*, order_items(*, products(name)), profiles:profiles!orders_customer_id_fkey(full_name, email, phone), driver:profiles!orders_driver_id_fkey(full_name, phone), shops(name, category)")
      .order("created_at", { ascending: false });
    if (error) {
      setLoadError(error.message);
      setLoading(false);
      return;
    }
    setLoadError(null);
    if (data) setOrders(data);
    setLoading(false);
  }

  // M1c-1: 'cancelled' routes through shop_cancel_order (FULL refund per
  // D1; admin passes its is_admin() arm) — the raw status write fired no
  // refund, one of the two extra money-loss paths found 2026-08-08.
  // Other statuses stay direct writes.
  async function changeStatus(order, status) {
    if (status === order.status) return;
    if (status === "cancelled") {
      if (!window.confirm(`Cancel order #${order.id.slice(0, 8).toUpperCase()}?\n\nAny verified payment is refunded IN FULL to the customer (shop-cancel rule).`)) return;
      const { error } = await supabase.rpc("shop_cancel_order", { p_order_id: order.id });
      if (error) { window.alert(`Cancel failed: ${error.message}`); return; }
      const { data: pend } = await supabase.from("refunds")
        .select("id").eq("order_id", order.id).eq("method", "pawapay").eq("status", "pending").maybeSingle();
      if (pend) {
        const { error: fnError } = await supabase.functions.invoke("pawapay-refund", { body: { orderId: order.id } });
        if (fnError) window.alert(`Order cancelled and refund recorded, but PawaPay submission failed: ${await fnErrorText(fnError)}\n\nUse Submit in the order's Refunds panel to retry.`);
      }
      setSelected(prev => prev && prev.id === order.id ? { ...prev, status: "cancelled" } : prev);
      loadOrders(); loadFailedRefunds(); loadContext(order.id);
      return;
    }
    await supabase.from("orders").update({ status }).eq("id", order.id);
    setSelected(prev => prev && prev.id === order.id ? { ...prev, status } : prev);
    loadOrders();
  }

  const statusColor = { pending: "#f39c12", confirmed: "#0057A8", ready_for_pickup: "#9b59b6", in_transit: "#00A651", delivered: "#27ae60", cancelled: "#e74c3c", cancel_requested: "#e67e22" };
  const orderTypeIcon = { marketplace: "🛍️", ride: "🚗", delivery: "🚴", hauling: "📦" };
  const fulfillmentIcon = { delivery: "🚴", shop_delivery: "🏪", pickup: "🚶" };

  const failedOrderIds = new Set(failedRefunds.map(r => r.order_id));
  const filtered = orders.filter(o =>
    (filter === "all" || (filter === "refund_failed" ? failedOrderIds.has(o.id) : o.status === filter)) &&
    (!search ||
      o.id.toLowerCase().includes(search.toLowerCase()) ||
      o.profiles?.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.shops?.name?.toLowerCase().includes(search.toLowerCase())
    )
  );

  const cell = { color: "#ccc", fontSize: 14, padding: "12px 16px" };

  return (
    <div>
      {selected && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSelected(null)}>
          <div style={{ background: "#111", border: "1px solid #333", borderRadius: 16, padding: 28, width: 520, maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ color: "#fff", margin: 0 }}>Order #{selected.id.slice(0,8).toUpperCase()}</h2>
              <button style={{ background: "none", border: "none", color: "#666", fontSize: 20, cursor: "pointer" }} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              {[
                { label: "Customer", value: selected.profiles?.full_name || "N/A" },
                { label: "Email", value: selected.profiles?.email || "N/A" },
                { label: "Phone", value: selected.profiles?.phone || "N/A" },
                { label: "Business", value: selected.shops?.name || "N/A" },
                { label: "Category", value: selected.shops?.category || "N/A" },
                { label: "Driver", value: selected.driver?.full_name ? `${selected.driver.full_name}${selected.driver.phone ? " · " + selected.driver.phone : ""}` : "Unassigned" },
                { label: "Order Type", value: selected.order_type || "N/A" },
                { label: "Fulfillment", value: selected.fulfillment_type || "delivery" },
                { label: "Payment", value: selected.payment_method?.replace("_"," ") || "N/A" },
                { label: "Payment Status", value: selected.payment_status || "N/A" },
                { label: "Total", value: "Le " + selected.total_amount?.toLocaleString() },
                { label: "Delivery Fee", value: "Le " + (selected.delivery_fee || 0).toLocaleString() },
                { label: "Date", value: new Date(selected.created_at).toLocaleString() },
              ].map(f => (
                <div key={f.label} style={{ background: "#1a1a1a", borderRadius: 8, padding: 12 }}>
                  <div style={{ color: "#666", fontSize: 11, marginBottom: 4, textTransform: "uppercase" }}>{f.label}</div>
                  <div style={{ color: "#fff", fontSize: 14, fontWeight: "600" }}>{f.value}</div>
                </div>
              ))}
            </div>
            {selected.delivery_address && selected.delivery_address !== "PICKUP" && (
              <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ color: "#666", fontSize: 11, marginBottom: 4, textTransform: "uppercase" }}>Delivery Address</div>
                <div style={{ color: "#fff", fontSize: 14 }}>{selected.delivery_address}</div>
              </div>
            )}
            {selected.pickup_code && (
              <div style={{ background: "#9b59b620", border: "1px solid #9b59b6", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ color: "#9b59b6", fontSize: 13, fontWeight: "600" }}>🚶 Pickup Code: <span style={{ fontSize: 20, letterSpacing: 4 }}>{selected.pickup_code}</span></div>
              </div>
            )}
            {selected.order_items?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: "#666", fontSize: 12, marginBottom: 8, textTransform: "uppercase" }}>Items</div>
                {selected.order_items.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", color: "#ccc", fontSize: 14, padding: "6px 0", borderBottom: "1px solid #222" }}>
                    <span>{item.products?.name || "Product"} x{item.quantity}</span>
                    <span>Le {(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <div style={{ color: "#666", fontSize: 12, marginBottom: 8, textTransform: "uppercase" }}>Update Status</div>
              <select style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 14, width: "100%", cursor: "pointer" }}
                value={selected.status} onChange={e => changeStatus(selected, e.target.value)}>
                {["pending","confirmed","ready_for_pickup","in_transit","delivered","cancelled",
                  ...(selected.status === "cancel_requested" ? ["cancel_requested"] : [])].map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
              </select>
            </div>

            <div style={{ marginTop: 20, borderTop: "1px solid #333", paddingTop: 16 }}>
              <div style={{ color: "#666", fontSize: 12, marginBottom: 8, textTransform: "uppercase" }}>Refunds</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <span style={{ background: "#1a1a1a", borderRadius: 8, padding: "6px 12px", color: "#ccc", fontSize: 12 }}>
                  Paid via: <b style={{ color: "#fff" }}>{selected.payment_method?.replace(/_/g, " ") || "N/A"}</b>
                </span>
                <span style={{ background: "#1a1a1a", borderRadius: 8, padding: "6px 12px", color: "#ccc", fontSize: 12 }}>
                  Payment status: <b style={{ color: "#fff" }}>{selected.payment_status || "N/A"}</b>
                </span>
              </div>
              {!refundCtx ? (
                <div style={{ color: "#666", fontSize: 13 }}>Loading refund context...</div>
              ) : refundCtx.error ? (
                <div style={{ color: "#e74c3c", fontSize: 13 }}>Refund context failed: {refundCtx.error}</div>
              ) : refundCtx.o_method == null ? (
                <div style={{ color: "#666", fontSize: 13 }}>No verified payment on this order — nothing to refund (cash or unpaid).</div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                    {[
                      { label: "Verified paid", value: refundCtx.o_verified_paid, color: "#fff" },
                      { label: "Refunded", value: refundCtx.o_completed, color: "#27ae60" },
                      { label: "In flight", value: refundCtx.o_pending, color: "#f39c12" },
                      { label: "Remainder", value: refundCtx.o_remainder, color: "#00A651" },
                    ].map(f => (
                      <div key={f.label} style={{ background: "#1a1a1a", borderRadius: 8, padding: 10 }}>
                        <div style={{ color: "#666", fontSize: 10, marginBottom: 3, textTransform: "uppercase" }}>{f.label}</div>
                        <div style={{ color: f.color, fontSize: 14, fontWeight: 700 }}>Le {Number(f.value).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  {refundCtx.o_has_failed && (
                    <div style={{ color: "#e74c3c", fontSize: 13, marginBottom: 10 }}>
                      ⚠ This order has a failed refund — the failure reason is in the banner on the Orders tab. Re-issue below.
                    </div>
                  )}
                  {refundCtx.o_credited && (
                    <div style={{ background: "#f39c1215", border: "1px solid #f39c12", borderRadius: 8, padding: 12, color: "#f39c12", fontSize: 13, lineHeight: 1.5, marginBottom: 10 }}>
                      ⚖ <b>Earnings credited.</b> Refunding this order will automatically REVERSE the
                      provider credit (goods-first). If the provider already withdrew, their balance goes
                      negative — a visible receivable that future earnings pay down first.
                    </div>
                  )}
                  {Number(refundCtx.o_pending) > 0 ? (
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ color: "#f39c12", fontSize: 13 }}>
                        A refund of Le {Number(refundCtx.o_pending).toLocaleString()} is already in flight.
                      </span>
                      {refundCtx.o_method === "pawapay" && (
                        <button disabled={refundBusy}
                          style={{ background: "#0057A8", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: refundBusy ? 0.5 : 1 }}
                          onClick={() => submitPendingRefund(selected.id)}>
                          Submit to PawaPay
                        </button>
                      )}
                    </div>
                  ) : Number(refundCtx.o_remainder) > 0 ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <input type="number" placeholder={`Amount (max ${Number(refundCtx.o_remainder)})`} value={refundAmt}
                        style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "9px 12px", color: "#fff", fontSize: 13, width: 150 }}
                        onChange={e => {
                          const v = e.target.value;
                          if (v === "") { setRefundAmt(""); return; }
                          const n = Math.max(0, Math.min(Number(v), Number(refundCtx.o_remainder)));
                          setRefundAmt(String(n));
                        }} />
                      <input placeholder="Reason (optional)" value={refundReason}
                        style={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "9px 12px", color: "#fff", fontSize: 13, flex: 1, minWidth: 160 }}
                        onChange={e => setRefundReason(e.target.value)} />
                      <button disabled={refundBusy || !(Number(refundAmt) > 0)}
                        style={{ background: "#00A651", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (refundBusy || !(Number(refundAmt) > 0)) ? 0.5 : 1 }}
                        onClick={doRefund}>
                        Refund Le {Number(refundAmt) > 0 ? Number(refundAmt).toLocaleString() : "…"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ color: "#27ae60", fontSize: 13 }}>Fully refunded.</div>
                  )}
                  {refundMsg && (
                    <div style={{ color: refundMsg.ok ? "#27ae60" : "#e74c3c", fontSize: 13, marginTop: 10 }}>{refundMsg.text}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {failedRefunds.length > 0 && (
        <div style={{ background: "#e74c3c15", border: "1px solid #e74c3c", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ color: "#e74c3c", fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            ⚠ {failedRefunds.length} failed refund{failedRefunds.length > 1 ? "s" : ""} — customers promised money that didn't arrive
          </div>
          {failedRefunds.map(r => {
            const o = orders.find(x => x.id === r.order_id);
            return (
              <div key={r.id} style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 13, padding: "7px 0", borderTop: "1px solid #e74c3c33" }}>
                <span style={{ color: "#00A651", fontFamily: "monospace", fontWeight: 600 }}>#{r.order_id.slice(0, 8).toUpperCase()}</span>
                <span style={{ color: "#fff", fontWeight: 600 }}>Le {Number(r.amount_sle).toLocaleString()}</span>
                <span style={{ color: "#e67e22", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.reason || ""}>
                  {r.reason || "No failure reason recorded"}
                </span>
                <button
                  style={{ background: "#e74c3c", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  onClick={() => o ? setSelected(o) : window.alert("Order not found in the loaded list")}>
                  Re-issue →
                </button>
              </div>
            );
          })}
        </div>
      )}

      {pendingRefunds.length > 0 && (
        <div style={{ background: "#f39c1215", border: "1px solid #f39c12", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ color: "#f39c12", fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            ⏳ {pendingRefunds.length} refund{pendingRefunds.length > 1 ? "s" : ""} minted but not sent to PawaPay
          </div>
          {pendingRefunds.map(r => {
            const o = orders.find(x => x.id === r.order_id);
            const ageMin = Math.round((Date.now() - new Date(r.created_at).getTime()) / 60000);
            const age = ageMin < 60 ? `${ageMin}m` : `${Math.round(ageMin / 60)}h`;
            return (
              <div key={r.id} style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 13, padding: "6px 0", borderTop: "1px solid #f39c1233" }}>
                <span style={{ color: "#00A651", fontFamily: "monospace", fontWeight: 600 }}>#{r.order_id.slice(0, 8).toUpperCase()}</span>
                <span style={{ color: "#fff", fontWeight: 600 }}>Le {Number(r.amount_sle).toLocaleString()}</span>
                <span style={{ color: "#f39c12", flex: 1 }}>waiting {age}</span>
                <button
                  style={{ background: "#f39c12", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  onClick={() => o ? setSelected(o) : window.alert("Order not found in the loaded list")}>
                  Open → Submit
                </button>
              </div>
            );
          })}
        </div>
      )}

      {sweepFails.length > 0 && (
        <div style={{ background: "#e74c3c15", border: "1px solid #e74c3c", borderRadius: 12, padding: 12, marginBottom: 20 }}>
          <div style={{ color: "#e74c3c", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            Sweep failures (7d): {sweepFails.length}
          </div>
          {sweepFails.map((f, i) => (
            <div key={i} style={{ color: "#ccc", fontSize: 12, padding: "3px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.error || ""}>
              {new Date(f.created_at).toLocaleString()} · {f.sweep} · {f.item_id?.slice(0, 8)} · {f.error}
            </div>
          ))}
        </div>
      )}

      {debts.length > 0 && (
        <div style={{ background: "#e67e2215", border: "1px solid #e67e22", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ color: "#e67e22", fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            Provider debts — refund reversals after payout (future earnings pay these down first)
          </div>
          {debts.map(d => (
            <div key={d.id} style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 13, padding: "6px 0", borderTop: "1px solid #e67e2233" }}>
              <span style={{ color: "#fff", fontWeight: 600 }}>{d.profiles?.full_name || d.profile_id?.slice(0, 8)}</span>
              <span style={{ color: "#e67e22", fontWeight: 700 }}>owes Le {Math.abs(d.balance).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {(wdRows.length > 0 || inflightBalance !== 0) && (
        <div style={{ background: "#0057A815", border: "1px solid #0057A8", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: wdRows.length ? 6 : 0 }}>
            <div style={{ color: "#0057A8", fontWeight: 700, fontSize: 14 }}>Withdrawals needing attention</div>
            <span style={{ color: inflightBalance === 0 ? "#27ae60" : "#f39c12", fontSize: 13, fontWeight: 600 }}>
              In flight: Le {inflightBalance.toLocaleString()} {inflightBalance === 0 ? "✓" : "(should trend to zero)"}
            </span>
          </div>
          {wdRows.map(w => {
            const ageMin = Math.round((Date.now() - new Date(w.created_at).getTime()) / 60000);
            const age = ageMin < 60 ? `${ageMin}m` : `${Math.round(ageMin / 60)}h`;
            const color = w.status === "failed" ? "#e74c3c" : w.status === "submitted" ? "#f39c12" : "#0057A8";
            return (
              <div key={w.id} style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 13, padding: "7px 0", borderTop: "1px solid #0057A833" }}>
                <span style={{ color: "#fff", fontWeight: 600 }}>Le {Number(w.amount_sle).toLocaleString()}</span>
                <span style={{ color: "#ccc" }}>{w.profiles?.full_name || "Provider"} · {w.phone}</span>
                <span style={{ color, fontWeight: 600 }}>{w.status}{w.status !== "failed" ? ` · ${age}` : ""}</span>
                <span style={{ color: "#e67e22", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={w.reason || ""}>
                  {w.status === "failed" ? (w.reason || "No failure reason recorded") : w.status === "submitted" ? "awaiting PawaPay callback" : "not yet sent to PawaPay"}
                </span>
                {w.status === "pending" && (
                  <button
                    style={{ background: "#0057A8", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    onClick={() => retryWithdrawal(w.id)}>
                    Retry send
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <input style={{ background: "#111", border: "1px solid #333", borderRadius: 8, padding: "10px 16px", color: "#fff", fontSize: 14, width: 280 }}
          placeholder="Search by ID, customer, or business..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["all","pending","confirmed","cancel_requested","in_transit","delivered","cancelled","refund_failed"].map(f => (
            <button key={f} style={{ background: filter === f ? (f === "refund_failed" ? "#e74c3c20" : "#00A65120") : "#111", border: filter === f ? (f === "refund_failed" ? "1px solid #e74c3c" : "1px solid #00A651") : "1px solid #333", borderRadius: 20, padding: "6px 14px", color: filter === f ? (f === "refund_failed" ? "#e74c3c" : "#00A651") : "#666", cursor: "pointer", fontSize: 13 }} onClick={() => setFilter(f)}>
              {f === "all" ? "All" : f.replace(/_/g," ")}
            </button>
          ))}
        </div>
        <span style={{ color: "#666", fontSize: 13, marginLeft: "auto" }}>{filtered.length} orders</span>
      </div>

      {loading ? <div style={{ color: "#666", textAlign: "center", padding: 40 }}>Loading orders...</div> : loadError ? (
        <div style={{ color: "#e74c3c", textAlign: "center", padding: 40, background: "#111", border: "1px solid #333", borderRadius: 12 }}>
          Failed to load orders: {loadError}
        </div>
      ) : (
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["Order ID","Customer","Business","Service","Fulfillment","Amount","Status","Date",""].map(h => (
                <th key={h} style={{ color: "#666", fontSize: 12, fontWeight: "600", textAlign: "left", padding: "12px 16px", borderBottom: "1px solid #222", textTransform: "uppercase", background: "#0a0a0a" }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map(order => (
                <tr key={order.id} style={{ borderBottom: "1px solid #1a1a1a", cursor: "pointer" }} onClick={() => setSelected(order)}>
                  <td style={{ ...cell, color: "#00A651", fontFamily: "monospace", fontWeight: "600" }}>#{order.id.slice(0,8).toUpperCase()}</td>
                  <td style={cell}>
                    <div style={{ fontWeight: "600", color: "#fff" }}>{order.profiles?.full_name || "N/A"}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{order.profiles?.phone || ""}</div>
                  </td>
                  <td style={cell}>
                    <div style={{ color: "#fff" }}>{order.shops?.name || "—"}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{order.shops?.category || ""}</div>
                  </td>
                  <td style={cell}>{orderTypeIcon[order.order_type] || "📦"} {order.order_type?.replace("_"," ") || "N/A"}</td>
                  <td style={cell}>{fulfillmentIcon[order.fulfillment_type] || "🚴"} {order.fulfillment_type || "delivery"}</td>
                  <td style={{ ...cell, fontWeight: "600", color: "#fff" }}>Le {order.total_amount?.toLocaleString()}</td>
                  <td style={cell}>
                    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "600", background: (statusColor[order.status] || "#999") + "20", color: statusColor[order.status] || "#999", textTransform: "capitalize" }}>
                      {order.status?.replace(/_/g," ")}
                    </span>
                  </td>
                  <td style={{ ...cell, fontSize: 12 }}>{new Date(order.created_at).toLocaleDateString()}</td>
                  <td style={cell}><span style={{ color: "#0057A8", fontSize: 12 }}>View →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
